import {
  AvatarGroup as MuiAvatarGroup,
  AvatarGroupProps as MuiAvatarGroupProps,
  avatarGroupClasses,
  styled,
} from '@mui/material';
import * as React from 'react';
import { AvatarProps, getHeightAndWidth } from 'src/components/Avatar/Avatar';

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  spacing?: MuiAvatarGroupProps['spacing'];
};

export type AvatarGroupProps = Omit<
  MuiAvatarGroupProps,
  keyof UnsupportedProps
> & {
  /**
   * The size of the component.
   * @default 'xsmall'
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
} & UnsupportedProps;

/**
 * AvatarGroup renders a stack of avatars with an overflow indicator.
 *
 * Demos:
 *
 * - [Avatar — Group](https://unify.workiva.org/components/avatar?tab=code#grouped)
 *
 * API:
 *
 * - [AvatarGroup API](https://unify.workiva.org/components/avatar?tab=props#avatargroup)
 */
export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  function AvatarGroup(props, forwardedRef) {
    const { children, ...rest } = props;

    return (
      <AvatarGroupStyled ref={forwardedRef} {...rest}>
        {React.Children.map(children, (el) => {
          if (React.isValidElement(el)) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return React.cloneElement(el as React.ReactElement<AvatarProps>, {
              size: props.size,
            });
          }
          return el;
        })}
      </AvatarGroupStyled>
    );
  }
);

const AvatarGroupStyled = styled(MuiAvatarGroup, {
  shouldForwardProp: (prop) => prop !== 'size',
})<AvatarGroupProps>(({ theme, size = 'xsmall' }) => {
  return {
    [`& .${avatarGroupClasses.avatar}`]: {
      ...getHeightAndWidth(size, theme),
      // Remove the outline implemented in avatarStyleOverrides since grouped avatars already have a border applied.
      boxShadow: 'none',
    },
  };
});

export default AvatarGroup;
